// Step 5 — Pricing-overzicht + Stripe-checkout-launch (T15, T16).
//
// "Naar betalen"-knop start de Stripe-checkout-redirect direct vanuit deze
// pagina. De aparte Step 6 is per april 2026 verwijderd — die toonde een extra
// bevestigingsscherm zonder meerwaarde.
//
// Bij Stripe-cancel landt de gebruiker op /signup/5?compagnyId=<id>; we
// rehydraten dan de context vanuit de URL (in-memory state is wiped door de
// round-trip via stripe.com).
//
// Partner-data komt uit Firestore via de orchestrator (één listener,
// gedistribueerd via OnboardingFlowContext). Stappen lezen `partner`
// rechtstreeks uit context — NIET zelf opnieuw subscriben en NIET via het
// hardcoded `PARTNER_PACKAGES`-map, anders raakt freeIncludedModules of
// fixedPackage uit de pas met de live-config.

import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import firebase from '../../../firebase/compat'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import useDevPreview from '../../../hooks/useDevPreview'
import MODULES from '../../../helpers/subscriptionHelpers/moduleDefinitions'
import calculateTotal from '../../../helpers/subscriptionHelpers/calculateTotal'
import spinner from '../../../images/spinner-ripple.svg'

const Step5Pricing = ({ onNext, onPrev }) => {

    const { compagnyId, setCompagnyId, modules, partner, partnerSlug } = useContext(OnboardingFlowContext)
    const { active: devPreview } = useDevPreview()
    const location = useLocation()

    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    // Stripe-cancel-redirect rehydration: in-memory context is wiped by de
    // round-trip via stripe.com. De cancel_url bevat compagnyId zodat we hier
    // de context kunnen herstellen.
    useEffect(() => {
        if (compagnyId) return
        const fromUrl = new URLSearchParams(location.search).get('compagnyId')
        if (fromUrl) setCompagnyId(fromUrl)
    }, [compagnyId, location.search, setCompagnyId])

    // Inbox bug #2 (2026-05-08): bij `/signup/5?partner=<slug>` is `partner` in
    // context op de eerste render nog null (`usePartner` heeft nog niet gefetched,
    // en de hoist-`useEffect` in OnboardingFlow.jsx loopt pas NA de eerste render
    // van children). Het seed-effect zet `partnerSlug` in context pas wanneer de
    // partner-fetch is settled — dus dat veld is een betrouwbaar signaal of de
    // partner-resolutie klaar is, los van success of empty-fetch.
    const urlPartnerSlug = new URLSearchParams(location.search).get('partner')
    const partnerStillResolving = !!urlPartnerSlug && !partnerSlug

    const activeSlugs = useMemo(
        () => Object.keys(modules || {}).filter((slug) => modules[slug] === true),
        [modules]
    )

    const freeIncluded = partner?.freeIncludedModules || []
    const monthlyTotal = useMemo(
        () => calculateTotal({ activeModules: modules, partner }),
        [modules, partner]
    )

    const startCheckout = async () => {
        setError('')

        // Dev-preview: skip Stripe-call helemaal, ga door naar Step 7
        // (die in dev-mode de webhook simuleert).
        if (devPreview) {
            onNext()
            return
        }

        if (!compagnyId) {
            setError('Geen actieve onboarding-sessie. Begin opnieuw bij Step 1.')
            return
        }

        setLoading(true)
        try {
            // Stripe-checkout neemt de gebruiker uit de React-app weg, dus de
            // in-memory OnboardingFlowContext is weg bij terugkeer. compagnyId
            // gaat mee via de URL zodat Step 7 (success) en Step 5 (cancel)
            // de context kunnen herstellen.
            const successUrl = `${window.location.origin}/signup/7?compagnyId=${compagnyId}`
            const cancelUrl = `${window.location.origin}/signup/5?compagnyId=${compagnyId}`

            const createCheckoutSession = firebase.functions().httpsCallable('createCheckoutSession')
            const result = await createCheckoutSession({
                client: compagnyId,
                activeModules: modules,
                partner: partnerSlug,
                successUrl,
                cancelUrl,
            })

            window.location.href = result.data.url
        } catch (err) {
            console.error('Step5Pricing createCheckoutSession error', err)
            setError(err.message || 'Stripe-checkout kon niet starten. Probeer het opnieuw.')
            setLoading(false)
        }
    }

    return (
        <div className="onboarding-step">
            <h1>Je abonnement</h1>
            <p className="onboarding-step-intro">
                Een overzicht van wat je hebt gekozen. In de volgende stap rond je de betaling af.
            </p>

            {partnerStillResolving ? (
                <div className="onboarding-step-loading">
                    <img src={spinner} alt="Partner-pakket laden…" />
                </div>
            ) : (
                <div className="onboarding-pricing-table">
                    {activeSlugs.length === 0 && (
                        <p className="onboarding-step-stub-note">
                            Geen modules geselecteerd. Ga terug om iets te kiezen.
                        </p>
                    )}

                    {activeSlugs.map((slug) => {
                        const mod = MODULES[slug]
                        const isFree = freeIncluded.includes(slug)
                        return (
                            <div key={slug} className="onboarding-pricing-row">
                                <span>{mod.name}</span>
                                <span className="onboarding-pricing-amount">
                                    {isFree ? <em>Inbegrepen</em> : `€${mod.price} /mnd`}
                                </span>
                            </div>
                        )
                    })}

                    <div className="onboarding-pricing-total">
                        <span>Totaal per maand</span>
                        <strong>€{monthlyTotal}</strong>
                    </div>
                </div>
            )}

            {error && <p className="onboarding-error">{error}</p>}

            <div className="onboarding-step-actions">
                <button
                    type="button"
                    className="onboarding-button-secondary"
                    onClick={onPrev}
                    disabled={loading}
                >
                    Terug
                </button>
                <button
                    type="button"
                    onClick={startCheckout}
                    disabled={loading || partnerStillResolving || (!devPreview && activeSlugs.length === 0)}
                >
                    {loading ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Naar betalen'}
                </button>
            </div>
        </div>
    )

}

export default Step5Pricing
